import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Alert,
  IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MapIcon from '@mui/icons-material/Map';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { useRepositories } from '@/infrastructure/repositories/useRepositories.js';
import { ReportGeneratorService } from '@/domain/report/ReportGeneratorService.js';
import { ReportExportService } from '@/domain/report/ReportExportService.js';
import { createMessageDictionaryService } from '@/domain/message/MessageDictionaryService.js';
import { useFavoriteStore } from '@/stores/useFavoriteStore.js';
import { buildDeepLink } from '@/hooks/useDeepLinkContext.js';
import { ValidationSeverity, type MessageEvent, type Report, type SimulationRun } from '@k-ucap/shared';

const dictionaryService = createMessageDictionaryService();

export const ReportsPage = () => {
  const selectedProject = useAuthStore((s) => s.selectedProject);
  const userProfile = useAuthStore((s) => s.userProfile);
  const navigate = useNavigate();
  const { reportRepository, simulationRunRepository, messageEventRepository } = useRepositories();
  const favorites = useFavoriteStore((s) => s.items);
  const toggleFavorite = useFavoriteStore((s) => s.toggle);
  const [reports, setReports] = useState<Report[]>([]);
  const [runs, setRuns] = useState<SimulationRun[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failureEvents, setFailureEvents] = useState<MessageEvent[]>([]);
  const [searchParams] = useSearchParams();

  const loadData = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    setError(null);
    try {
      const [loadedReports, loadedRuns] = await Promise.all([
        reportRepository.findByProjectId(selectedProject.id),
        simulationRunRepository.findByProjectId(selectedProject.id),
      ]);
      setReports(loadedReports);
      setRuns(loadedRuns);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [selectedProject, reportRepository, simulationRunRepository]);

  useEffect(() => {
    const reportParam = searchParams.get('report') ?? searchParams.get('reportId');
    const runParam = searchParams.get('run') ?? searchParams.get('runId');
    if (reportParam) setSelectedReportId(reportParam);
    if (runParam) setSelectedRunId(runParam);
  }, [searchParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectedReport = useMemo(() => reports.find((r) => r.id === selectedReportId), [reports, selectedReportId]);

  // Prompt 14 Phase 3: Failure evidence deep link data
  useEffect(() => {
    if (!selectedReport) {
      setFailureEvents([]);
      return;
    }
    messageEventRepository
      .findByRunId(selectedReport.simulationRunId)
      .then((events) => {
        setFailureEvents(
          events.filter((e) => e.validation === ValidationSeverity.FAIL || e.validation === ValidationSeverity.WARN)
        );
      })
      .catch(() => setFailureEvents([]));
  }, [selectedReport, messageEventRepository]);

  const handleGenerate = async () => {
    if (!selectedProject || !selectedRunId || !userProfile) return;
    setLoading(true);
    setError(null);
    try {
      const run = runs.find((r) => r.id === selectedRunId);
      if (!run) throw new Error('Run not found');
      const events = await messageEventRepository.findByRunId(run.id);
      const definitions = dictionaryService.getAllMessages();
      const report = ReportGeneratorService.generate({ run, events, definitions });
      await reportRepository.save(report);
      await loadData();
      setSelectedReportId(report.id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (format: 'csv' | 'json' | 'html') => {
    if (!selectedReport) return;
    let content = '';
    let mimeType = 'text/plain';
    let extension = 'txt';
    if (format === 'csv') {
      content = ReportExportService.toCsv(selectedReport);
      mimeType = 'text/csv';
      extension = 'csv';
    } else if (format === 'json') {
      content = ReportExportService.toJson(selectedReport);
      mimeType = 'application/json';
      extension = 'json';
    } else if (format === 'html') {
      content = ReportExportService.toHtml(selectedReport);
      mimeType = 'text/html';
      extension = 'html';
    }
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedReport.title}.${extension}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (!selectedReport) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(ReportExportService.toHtml(selectedReport));
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        보고서
      </Typography>
      {!selectedProject && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          프로젝트를 선택해야 보고서를 조회할 수 있습니다.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>실행 이력 선택</InputLabel>
            <Select
              value={selectedRunId ?? ''}
              label="실행 이력 선택"
              onChange={(e) => setSelectedRunId(e.target.value as string)}
            >
              {runs.map((run) => (
                <MenuItem key={run.id} value={run.id}>
                  {run.name} ({new Date(run.startedAt).toLocaleString()})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleGenerate} disabled={!selectedRunId || loading}>
            보고서 생성
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>
            새로고침
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              보고서 목록
            </Typography>
            {reports.length === 0 ? (
              <Typography color="text.secondary">생성된 보고서가 없습니다.</Typography>
            ) : (
              <List dense>
                {reports.map((report) => {
                  const favoriteKey = `REPORT:${report.id}`;
                  const isFavorite = !!favorites[favoriteKey];
                  return (
                    <ListItem
                      key={report.id}
                      disablePadding
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite({
                              id: favoriteKey,
                              assetType: 'REPORT',
                              assetId: report.id,
                              name: report.title,
                              projectId: report.projectId,
                              href: `/reports?report=${report.id}`,
                            });
                          }}
                        >
                          {isFavorite ? <StarIcon color="warning" /> : <StarBorderIcon />}
                        </IconButton>
                      }
                    >
                      <ListItemButton
                        selected={selectedReportId === report.id}
                        onClick={() => setSelectedReportId(report.id)}
                      >
                        <ListItemText primary={report.title} secondary={new Date(report.createdAt).toLocaleString()} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          {selectedReport ? (
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography variant="h6">{selectedReport.title}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button size="small" startIcon={<DownloadIcon />} onClick={() => handleExport('csv')}>
                      CSV
                    </Button>
                    <Button size="small" startIcon={<DownloadIcon />} onClick={() => handleExport('json')}>
                      JSON
                    </Button>
                    <Button size="small" startIcon={<DownloadIcon />} onClick={() => handleExport('html')}>
                      HTML
                    </Button>
                    <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={handlePrint}>
                      Print
                    </Button>
                  </Box>
                </Box>
                <Typography variant="subtitle2">Summary</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Chip label={`Total: ${selectedReport.summary.totalMessages}`} />
                  <Chip label={`PASS: ${selectedReport.summary.passCount}`} color="success" />
                  <Chip label={`WARN: ${selectedReport.summary.warnCount}`} color="warning" />
                  <Chip label={`FAIL: ${selectedReport.summary.failCount}`} color="error" />
                </Box>
                <Typography variant="subtitle2">Message Type Statistics</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Count</TableCell>
                      <TableCell>PASS</TableCell>
                      <TableCell>WARN</TableCell>
                      <TableCell>FAIL</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(selectedReport.messageTypeStats).map(([type, s]) => (
                      <TableRow key={type}>
                        <TableCell>#{type}</TableCell>
                        <TableCell>{s.count}</TableCell>
                        <TableCell>{s.pass}</TableCell>
                        <TableCell>{s.warn}</TableCell>
                        <TableCell>{s.fail}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  Failure Evidence ({failureEvents.length})
                </Typography>
                {failureEvents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    FAIL/WARN 이벤트가 없습니다.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Time</TableCell>
                        <TableCell>Message</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {failureEvents.slice(0, 50).map((event) => (
                        <TableRow key={event.id}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {new Date(event.timestamp).toLocaleTimeString()}
                          </TableCell>
                          <TableCell>
                            #{event.messageType} {event.name}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={event.validation}
                              size="small"
                              color={event.validation === 'FAIL' ? 'error' : 'warning'}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Button
                                size="small"
                                startIcon={<OpenInNewIcon />}
                                onClick={() =>
                                  navigate(
                                    buildDeepLink('/analyzer', {
                                      runId: selectedReport.simulationRunId,
                                      messageId: event.id,
                                      timestamp: event.timestamp,
                                    })
                                  )
                                }
                              >
                                Analyzer
                              </Button>
                              {[101, 104, 110, 1100].includes(event.messageType) && (
                                <Button
                                  size="small"
                                  startIcon={<MapIcon />}
                                  onClick={() =>
                                    navigate(
                                      buildDeepLink('/gis', {
                                        vehicleId: (event.fields?.vehicleId as string | undefined) ?? null,
                                        timestamp: event.timestamp,
                                      })
                                    )
                                  }
                                >
                                  GIS
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">보고서를 선택하거나 새로 생성하세요.</Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReportsPage;
